import { useEffect, useState } from 'preact/hooks'

import { useSearchspringContext, useSearchspringFetcher } from '../fetchers/provider'
import { SearchspringSuggestProvider } from '../fetchers/suggest'
/* global GM_STATE */
import { SearchPageLink } from './SearchPageLink'
import { QuickSearchSuggestions } from './QuickSearchSuggestions'
import { SearchspringTrendingProvider } from '../fetchers/trending'
import { QuickSearchResults } from './QuickSearchResults'
import { SearchspringRecommendProvider } from '../fetchers/recommend'
import { SearchspringAutocomplete, SearchspringAutocompleteProvider } from '../fetchers/autocomplete'

import { normaliseResults } from '../helpers/normalisers'
import clsx from 'clsx'

const popularProductsProfile = GM_STATE?.siteIds?.popularProductsProfile || 'api-test'

export function QuickSearchDropdown({ query, inputValue, correctedQuery, setCorrectedQuery, setRedirect, redirect }) {
  const [heading, setHeading] = useState('Popular Searches')
  const [hasResults, setHasResults] = useState(false)
  const { results: queryResults, refine } = useSearchspringContext()
  const { results, refine: autocompleteRefine } = useSearchspringFetcher(new SearchspringAutocomplete(), {
    runOnMount: false,
    options: {
      query,
      resultsPerPage: 4,
      category: 'products'
    },
    onFetch: (results) => {
      return {
        ...results,
        results: normaliseResults(results.results)
      }
    }
  })

  useEffect(() => {
    const hasEmptyQuery = query === 'null' || query === 'undefined' || !query
    const hasNoResults = results?.results?.length === 0
    setHasResults(!hasNoResults && !hasEmptyQuery)

    if (results?.merchandising?.redirect && !correctedQuery) {
      setRedirect(results.merchandising.redirect)
    }

    setHeading(() => {
      if (hasEmptyQuery || hasNoResults) return 'Popular'
      else return 'Suggested'
    })

    if (!hasNoResults) return
    const suggestedQuery = queryResults?.suggested?.text
    if (suggestedQuery && suggestedQuery !== correctedQuery && suggestedQuery !== query) {
      setCorrectedQuery(suggestedQuery)
      autocompleteRefine('query', suggestedQuery)
    }
  }, [query, results?.results])

  useEffect(() => {
    setCorrectedQuery(false)
    refine('query', query)
    autocompleteRefine('query', query)
  }, [query])

  // Trigger immediate search when inputValue changes
  useEffect(() => {
    if (inputValue && inputValue !== query) {
      autocompleteRefine('query', inputValue)
    }
  }, [inputValue])

  return (
    <>
      <QueryHeader hasResults={hasResults} query={query} correctedQuery={correctedQuery} redirect={redirect} />
      <div className="flex flex-col overflow-y-scroll transition-transform -translate-y-4 group-data-visible:translate-y-0 md:flex-row">
        <div className="flex flex-col justify-start px-4 py-6 pt-2 border-b shrink-0 border-neutrals-light-grey md:w-80 md:p-10">
          <p className="pb-4 h4 shrink-0 text-primary-blue">{heading} Searches</p>
          {query && hasResults ? (
            <SearchspringSuggestProvider category="collections">
              <QuickSearchSuggestions type="search" query={query} />
            </SearchspringSuggestProvider>
          ) : (
            <SearchspringTrendingProvider category="collections">
              <QuickSearchSuggestions type="search" />
            </SearchspringTrendingProvider>
          )}
          <hr className="my-6 -mx-4 md:mx-0 md:my-10" />
          <p className="pb-4 h4 shrink-0 text-primary-blue">{heading} Pages</p>
          {query ? (
            <SearchspringAutocompleteProvider resultsPerPage={3} category="pages">
              <QuickSearchSuggestions type="collection" query={query} />
            </SearchspringAutocompleteProvider>
          ) : (
            <SearchspringTrendingProvider category="pages">
              <QuickSearchSuggestions type="search" />
            </SearchspringTrendingProvider>
          )}
        </div>
        <div
          className={clsx(
            'flex grow flex-col gap-6 py-6 md:gap-10 md:py-10',
            hasResults && 'order-first md:order-none pb-0'
          )}
        >
          <div className="flex justify-between px-4 md:px-10">
            <p className="h4 shrink-0 text-primary-blue">
              {heading} Products
              {!hasResults ? null : <span> ({results?.pagination?.totalResults})</span>}
            </p>
            {!hasResults ? null : (
              <SearchPageLink
                type={'search'}
                query={correctedQuery || query}
                className="hidden btn btn-md btn-solid-black md:flex"
              >
                View {results?.pagination?.totalResults} products
              </SearchPageLink>
            )}
          </div>
          <div className="md:px-10">
            {hasResults ? (
              <QuickSearchResults parentResults={results} />
            ) : (
              <SearchspringRecommendProvider profile={popularProductsProfile} limit={4}>
                <QuickSearchResults />
              </SearchspringRecommendProvider>
            )}
          </div>
          {!hasResults ? null : (
            <div class="sticky bottom-0 z-20 bg-primary-white p-4 pb-8 md:hidden">
              <SearchPageLink type={'search'} query={correctedQuery || query} className="btn btn-md btn-solid-black">
                View {results?.pagination?.totalResults} products
              </SearchPageLink>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const QueryHeader = ({ hasResults, query, correctedQuery, redirect }) => {
  if (hasResults && !correctedQuery) return null
  if (!hasResults && (query === 'null' || query === 'undefined' || !query)) return null

  const TextContent = () => {
    return (
      <p>
        No results found for <b>{query}.</b>
        {correctedQuery ? (
          <>
            &nbsp;showing results for <b>{correctedQuery}</b> instead.
          </>
        ) : redirect ? (
          <>&nbsp;<a className={'font-medium underline whitespace-normal transition-colors duration-150 underline-offset-4 hover:text-primary-blue'} href={redirect}>Search for {redirect.split('/').pop().replaceAll('-', ' ')}?</a></>
        ) : (
          <>&nbsp;Please try another search</>
        )}
      </p>
    )
  }
  return (
    <div className="flex justify-center p-4 bg-system-error/10">
      <TextContent />
    </div>
  )
}
